import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/apiFetch";
import { Button } from "@/components/ui/button";

type Source = { document_id: number; filename: string; current: boolean; url: string | null };
type Result = { job: null | { status: string; error_message: string | null }; completed: number; total: number;
  entries?: { data: { id: number; statement: string; status: string; sources: Source[] }[]; last_page: number } };

function heading(statement: string) {
  const firstLine = statement.split("\n").find(line => line.trim()) || "Report section";
  const text = firstLine.replace(/^\s*#{1,6}\s*/, "").replace(/\*\*/g, "").trim();
  return text.length > 140 ? `${text.slice(0, 137)}…` : text;
}

export default function ReportSources({ reportId }: { reportId: number }) {
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const next = await apiFetch<Result>(`/api/project-analyses/${reportId}/sources`);
        for (let page = 2; page <= (next.entries?.last_page || 1); page++) {
          if (stopped) return;
          const more = await apiFetch<Result>(`/api/project-analyses/${reportId}/sources?page=${page}`);
          next.entries!.data.push(...(more.entries?.data || []));
        }
        if (stopped) return;
        setResult(next);
        setError("");
        if (next.job && ["queued", "processing"].includes(next.job.status)) timer = setTimeout(load, 5000);
      } catch (e) { if (!stopped) setError(e instanceof Error ? e.message : "Could not load sources"); }
    };
    load();
    return () => { stopped = true; clearTimeout(timer); };
  }, [reportId, revision]);
  const start = async () => {
    setBusy(true); setError("");
    try {
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      await apiFetch(`/api/project-analyses/${reportId}/sources`, { method: "POST", body: { refresh: result?.job?.status === "completed" } });
      setRevision(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not start source lookup"); }
    finally { setBusy(false); }
  };
  const download = async (source: Source) => {
    if (!source.url) return;
    setDownloadError("");
    try {
      const blob = await apiFetch<Blob>(source.url, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = source.filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setDownloadError(e instanceof Error ? e.message : "Could not download source"); }
  };
  const running = !!result?.job && ["queued", "processing"].includes(result.job.status);
  return <section className="border-t pt-4 space-y-3">
    <h3 className="font-semibold">Report sources</h3>
    <Button variant="outline" onClick={start} disabled={busy || running || !result}>
      {running ? "Finding sources…" : result?.job?.status === "completed" ? "Recheck sources" : result?.job?.status === "failed" ? "Retry source lookup" : "Find sources for this report"}
    </Button>
    {error && <p role="alert" className="text-red-600">{error}</p>}
    {error && <Button variant="outline" onClick={() => setRevision(v => v + 1)}>Reload source status</Button>}
    {downloadError && <p role="alert" className="text-red-600">{downloadError}</p>}
    {result?.job && <p className="text-sm">{result.job.status} · {result.completed}/{result.total} entries checked{result.job.status === "queued" ? ". Waiting for the analysis worker." : ""}</p>}
    {result?.job?.error_message && <p role="alert" className="text-red-600">{result.job.error_message}</p>}
    {!!result?.entries?.data.length && <details className="border rounded p-3">
      <summary className="cursor-pointer">Sources</summary>
      <div className="mt-3 space-y-4">
        {result.entries.data.map(entry => <div key={entry.id} className="text-sm">
          <h4 className="font-medium">{heading(entry.statement)}</h4>
          {entry.sources.filter(source => source.current && source.url).map((source, index) =>
            <button key={index} type="button" className="block text-primary underline text-left" onClick={() => download(source)}>{source.filename}</button>)}
          {!entry.sources.some(source => source.current && source.url) && <p>{entry.status === "completed" ? "Source not verified" : "Pending"}</p>}
        </div>)}
      </div>
    </details>}
  </section>;
}
